// ============================================================
// Viashift — Permission helper, PWA (Phase 3, Epic 3D.1)
// ============================================================
// Sync API over the permission cache stored on SessionValue.
// Components and event handlers call hasPermission(session, name)
// and receive an immediate boolean. Mirrors the existing
// isAtLeast(role, required) pattern from src/lib/roles.ts —
// callers thread the session in rather than the helper itself
// being a hook.
//
// The cache is populated at session-load time in App.tsx via
// defaultPermissionsForRole(activeMembership.role). Today this
// matches what the SQL has_permission() function would return
// (both compute from the same catalog), so the PWA can resolve
// every check sync without a database round-trip.
//
// When 7D's permission_grants table lands, the cache populates
// from a one-time fetch at session load instead of from
// defaults. The hasPermission() signature does not change.
//
// 3D.2 addition:
//
//   - realRoleHasPermission(session, name) reads against the
//     real role's catalog defaults, bypassing the viewAsRole
//     impersonation. Used by surfaces that must stay visible
//     while impersonating (chiefly the impersonation picker
//     itself — without this, selecting "view as Staff" would
//     hide the toggle that gets you back out).
// ============================================================

import type { SessionValue } from '@/lib/session';
import type { PermissionName } from './catalog';
import { PERMISSION_CATALOG } from './catalog';
import type { ClinicRole } from '@/lib/session';

export type { PermissionName } from './catalog';
export {
  PERMISSION_CATALOG,
  defaultPermissionsForRole,
} from './catalog';

/**
 * True iff the given session's active user/clinic holds the
 * named permission. Pass the session from useSession() (or from
 * the store directly when the session may be absent).
 *
 * Resolves against the EFFECTIVE permission set on SessionValue,
 * which reflects viewAsRole impersonation when active. This is
 * almost always the right read for UI gating.
 *
 * Authenticated-layout components:
 *
 *   const session = useSession();
 *   const canEdit = hasPermission(session, 'reorder.draft.edit');
 *
 * Public-layout / optional-session components:
 *
 *   const session = useSessionStore((s) => s.session);
 *   if (!session) return null;
 *   if (!hasPermission(session, 'reorder.approve')) return null;
 */
export function hasPermission(
  session: SessionValue,
  permission: PermissionName,
): boolean {
  return session.permissions.has(permission);
}

/**
 * True iff the user's REAL role satisfies the named permission,
 * regardless of viewAsRole impersonation. Reads directly from
 * the catalog defaults — does not consult the SessionValue
 * permission cache.
 *
 * Use sparingly; only for surfaces that must remain visible
 * during impersonation. The canonical caller is the View-as
 * picker in Header: without this, selecting an impersonated
 * role would drop the user's effective permissions below the
 * picker's gate, hiding the affordance that lets them exit.
 *
 * Forward-compat with 7D: when permission_grants lands, the
 * "real role permissions" set will be a second cached set on
 * SessionValue populated alongside `permissions`; this helper
 * gets repointed at that field. Callers don't change.
 */
export function realRoleHasPermission(
  session: SessionValue,
  permission: PermissionName,
): boolean {
  const entry = PERMISSION_CATALOG[permission];
  return (entry.defaultRoles as readonly ClinicRole[]).includes(
    session.activeMembership.role,
  );
}
